import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  LocationOn as LocationOnIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  Search,
} from "@mui/icons-material";
import { useHistory, useLocation } from "react-router-dom";
import { customerService } from '../../services/customer';
import { getAssociates } from '../../services/associate';
import { getStates, getCities, getLeadInterested, getBudgets, getSources, getInquiryStatuses, getLeadStatuses } from '../../services/master';
import { leadService } from '../../services/lead';
import AlertModal from '../../components/AlertModal';
import ConfirmModal from '../../components/ConfirmModal';

// Fallback options removed - now using dynamic API data

const interestedInOptions = [
  "Plot",
  "Premium Plots",
  "Luxury Villas",
  "Modern Apartments",
  "Commercial Spaces",
  "Residential Complex",
  "1 BHK",
  "2 BHK",
  "3 BHK",
  "4 BHK",
];

const budgetOptions = [
  "10-25 Lakhs",
  "25-50 Lakhs",
  "50-75 Lakhs",
  "75 Lakhs - 1 Crore",
  "1-2 Crores",
  "2-5 Crores",
  "Above 5 Crores",
  "25 Lakhs",
];

const stateOptions = [
  "Andhra Pradesh",
  "Karnataka",
  "Kerala",
  "Tamil Nadu",
  "Telangana",
  "Maharashtra",
  "Gujarat",
  "Delhi",
  "Uttar Pradesh",
  "Rajasthan",
];

const mapCustomer = (item) => {
  const basicRaw = item?.basicDetails;
  const personalRaw = item?.personalDetails;
  const otherRaw = item?.otherDetails;

  const basic =
    basicRaw && typeof basicRaw === 'object' && !Array.isArray(basicRaw)
      ? basicRaw
      : (Array.isArray(basicRaw) ? basicRaw[0] : undefined) || {};
  const personal =
    personalRaw && typeof personalRaw === 'object' && !Array.isArray(personalRaw)
      ? personalRaw
      : (Array.isArray(personalRaw) ? personalRaw[0] : undefined) || {};
  const other =
    otherRaw && typeof otherRaw === 'object' && !Array.isArray(otherRaw)
      ? otherRaw
      : (Array.isArray(otherRaw) ? otherRaw[0] : undefined) || {};

  const id = item?._id ?? '';
  const fallbackId = item?.customerNo
    ? `CUST${String(item.customerNo).padStart(4, '0')}`
    : id
      ? `CUST${id.slice(-6).toUpperCase()}`
      : 'CUST-NA';

  return {
    id,
    customerId: item?.customerId || fallbackId,
    name: item?.name ?? '-',
    fatherName: basic?.fatherName ?? basic?.father ?? '-',
    profession: item?.profession ?? '-',
    mobile: item?.phone ?? '-',
    whatsappNumber: item?.whatsapp ?? '-',
    email: item?.email ?? '-',
    city: item?.city ?? '-',
    state: item?.state ?? '-',
    address: other?.address ?? other?.createdDetail ?? '-',
    dateOfBirth: personal?.dob ?? '-',
    age: personal?.age ?? '',
    anniversary: personal?.anniversary ?? '-',
    maritalStatus: personal?.maritalStatus ?? basic?.maritalStatus ?? item?.maritalStatus ?? 'Single',
    gender: basic?.gender ?? '-',
    bloodGroup: basic?.bloodGroup ?? '-',
    joinDate: item?.joinDate ?? '-',
    createdDate: item?.createdAt ? new Date(item.createdAt).toLocaleString('en-IN') : '-',
    createdBy: item?.createdBy ?? '-',
    associateName: item?.associateName ?? '-',
    status: item?.isDeleted ? 'Inactive' : 'Active',
    totalPurchases: typeof item?.totalPurchases === 'number'
      ? item.totalPurchases
      : Array.isArray(item?.purchaseHistory)
        ? item.purchaseHistory.length
        : Number.isFinite(item?.customerNo)
          ? item.customerNo
          : 0,
    totalSpent: typeof item?.totalSpent === 'number'
      ? item.totalSpent
      : Array.isArray(item?.purchaseHistory)
        ? item.purchaseHistory.reduce(
          (sum, entry) => sum + (typeof entry?.amount === 'number' ? entry.amount : 0),
          0,
        )
        : 0,
    aadharNumber: other?.aadhar ?? '-',
    panNumber: personal?.pan ?? '-',
    occupation: item?.profession ?? '-',
    companyName: item?.companyName ?? '-',
    annualIncome: item?.annualIncome ?? '-',
    emergencyContact: other?.emergencyContact ?? other?.salaryRange ?? '-',
    emergencyContactName: other?.emergencyContactName ?? other?.createdDetail ?? '-',
    relation: other?.relation ?? basic?.relation ?? '-',
    propertyInterest: item?.interestedIn ?? other?.createdDetail ?? '-',
    budget: item?.budget ?? '-',
    preferredLocation: other?.preferredLocation ?? '-',
    remark: other?.remark ?? item?.notes ?? '-',
    salaryRange: other?.salaryRange ?? '',
    source: other?.createdDetail ?? '',
    image:
      item?.image ||
      `https://ui-avatars.com/api/?name=${encodeURIComponent(item?.name ?? 'Customer')}&background=536DFE&color=fff`,
    purchaseHistory: Array.isArray(item?.purchaseHistory) ? item.purchaseHistory : [],
  };
};

const EditLead = () => {
  const history = useHistory();
  const location = useLocation();
  const leadData = location.state?.leadData;

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    whatsapp: "",
    company: "",
    designation: "",
    source: "",
    status: "New",
    priority: "Medium",
    interestedIn: "",
    budget: "",
    location: "",
    city: "",
    state: "",
    address: "",
    assignedTo: "",
    assignedToId: "",
    assignedMobile: "",
    nextFollowUp: "",
    followUpDate: new Date().toISOString().slice(0, 16),
    followUpStatus: "HOT",
    requirement: "",
    remark: "",
    reference: "",
    isActive: true,
  });

  const [leadInterestedOptions, setLeadInterestedOptions] = useState([]);
  const [sources, setSources] = useState([]);
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [inquiryStatuses, setInquiryStatuses] = useState([]);
  const [budgetOptionsFromApi, setBudgetOptionsFromApi] = useState([]);

  const [serverError, setServerError] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [errors, setErrors] = useState({});
  const [mobileError, setMobileError] = useState('');
  const [whatsappError, setWhatsappError] = useState('');

  const [alertData, setAlertData] = useState({ open: false, message: '', type: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    confirmText: '',
    cancelText: '',
    type: 'warning',
    action: null
  });

  const showAlert = (message, type = 'info') => setAlertData({ open: true, message, type });

  const validateMobile = (mobile) => {
    if (!mobile) return false;
    const mobileRegex = /^[6-9]\d{9}$/;
    return mobileRegex.test(mobile);
  };

  // Customer selection state
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);

  // Associate selection state
  const [associates, setAssociates] = useState([]);
  const [loadingAssociates, setLoadingAssociates] = useState(false);
  const [selectedAssociate, setSelectedAssociate] = useState(null);
  const [associateSearchOpen, setAssociateSearchOpen] = useState(false);

  const meta = useMemo(() => {
    if (!leadData) return {};
    return {
      id: leadData.id || leadData._id,
      _id: leadData._id,
      leadNo: leadData.leadNo,
      createdAt: leadData.createdAt,
      updatedAt: leadData.updatedAt,
      projectInterestedIn: Array.isArray(leadData?.projectInterestedIn) ? leadData.projectInterestedIn : [],
      stage: leadData?.stage || "Lead",
      followUp: leadData?.followUp || {},
      remarks: leadData?.remarks || {},
      assignedTo: leadData?.assignedTo,
      associateId: leadData?.associateId,
      reference: leadData?.reference,
      createdBy: leadData?.createdBy,
      status: leadData?.status,
    };
  }, [leadData]);

  // Fetch customers for selection
  const fetchCustomers = async () => {
    setLoadingCustomers(true);
    try {
      const data = await customerService.getCustomers();
      const list = Array.isArray(data) ? data.map(mapCustomer) : [];
      setCustomers(list);
    } catch (err) {
      console.error('Failed to fetch customers:', err);
    } finally {
      setLoadingCustomers(false);
    }
  };

  const fetchAssociates = async () => {
    setLoadingAssociates(true);
    try {
      const response = await getAssociates();
      const list = Array.isArray(response?.data)
        ? response.data
        : (response?.data?.docs || response?.docs || []);
      const formatted = list.map(a => ({
        id: a._id,
        associateId: a.associateId,
        name: a.name || '',
        mobile: a.phone || a.mobile || '',
        displayLabel: `${a.name || ''} — ${a.phone || a.mobile || ''}`,
        fullData: a
      }));
      setAssociates(formatted);
    } finally {
      setLoadingAssociates(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
    fetchAssociates();

    const loadMasters = async () => {
      try {
        const [interested, inquiryStatusData, budgets, sourcesData, leadStatusData] = await Promise.all([
          getLeadInterested(),
          getInquiryStatuses(),
          getBudgets(),
          getSources(),
          getLeadStatuses()
        ]);

        setLeadInterestedOptions(interested);
        setLeadStatuses(leadStatusData);

        // Filter by stageType 'Lead' and handle unique statuses
        const leadStatusesData = (inquiryStatusData || []).filter(item => (item.stageType || '').toLowerCase() === 'lead');
        const unique = Array.from(new Map(leadStatusesData.map(item => [(item.statusName || '').trim().toUpperCase(), item])).values());
        setInquiryStatuses(unique);

        setBudgetOptionsFromApi(budgets);
        setSources(sourcesData);
      } catch (err) {
        console.error("Failed to load masters:", err);
      }
    };
    loadMasters();
  }, []);

  useEffect(() => {
    if (leadData) {
      const safeStr = (val) => (typeof val === 'string' && val === '-' ? '' : val || "");
      const extName = (obj) => typeof obj === 'object' ? obj?.name || "" : safeStr(obj);
      const extPhone = (obj) => typeof obj === 'object' ? obj?.phone || obj?.mobile || "" : safeStr(obj);
      const safeDateTime = (val) => {
        if (!val || val === "-") return "";
        try { return new Date(val).toISOString().slice(0, 16); } catch { return ""; }
      };

      setFormData({
        name: leadData.customerDetails?.name || safeStr(leadData.name),
        email: leadData.customerDetails?.email || safeStr(leadData.email),
        phone: leadData.customerDetails?.phone || leadData.contact?.phone || safeStr(leadData.phone),
        whatsapp: leadData.customerDetails?.whatsapp || leadData.contact?.whatsapp || safeStr(leadData.whatsapp),
        company: leadData.customerDetails?.company || safeStr(leadData.company),
        designation: leadData.customerDetails?.profession || leadData.profession || safeStr(leadData.designation),
        source: safeStr(leadData.source),
        status: (() => {
          const raw = safeStr(leadData.status);
          const match = leadStatuses.find(s => (s.leadStatus || s.name || '').toUpperCase() === raw.toUpperCase());
          return match ? (match.leadStatus || match.name) : (raw || "New");
        })(),
        priority: safeStr(leadData.priority) || "Medium",
        interestedIn: safeStr(leadData.interestedIn),
        budget: leadData.budgetUpto || safeStr(leadData.budget),
        location: leadData.address?.addressLine || safeStr(leadData.location) || (typeof leadData.address === 'string' ? safeStr(leadData.address) : ""),
        city: leadData.address?.city || safeStr(leadData.city),
        state: leadData.address?.state || safeStr(leadData.state),
        address: leadData.address?.addressLine || (typeof leadData.address === 'string' ? safeStr(leadData.address) : "") || safeStr(leadData.location),
        assignedTo: leadData.associateDetails?.name || leadData.assignedTo?.name || extName(leadData.assignedTo),
        assignedToId: leadData.associateId || leadData.assignedToId || "",
        assignedMobile: leadData.associateDetails?.phone || leadData.assignedTo?.phone || extPhone(leadData.assignedMobile) || extPhone(leadData.assignedTo),
        nextFollowUp: safeDateTime(leadData.followUp?.nextFollowUpDate || leadData.nextFollowUp),
        followUpDate: safeDateTime(leadData.followUp?.lastFollowUpDate) || new Date().toISOString().slice(0, 16),
        followUpStatus: (() => {
          const raw = leadData.followUp?.followUpType || "";
          const match = inquiryStatuses.find(s => (s.statusName || '').toUpperCase() === raw.toUpperCase());
          return match ? match.statusName : (raw || "HOT");
        })(),
        requirement: leadData.remarks?.remark1 || safeStr(leadData.requirement),
        remark: leadData.remarks?.generalRemark || safeStr(leadData.notes),
        reference: safeStr(leadData.reference),
        isActive: typeof leadData.isActive === "boolean" ? leadData.isActive : true,
      });

      // Set selected customer if customerId exists in lead data
      if (leadData.customerId && customers.length > 0) {
        const customer = customers.find(c => c._id === leadData.customerId || c.id === leadData.customerId);
        if (customer) {
          setSelectedCustomer(customer);
        }
      }

      // Set selected associate if associateId exists in lead data
      // Set selected associate if assignment exists in lead data
      const aid = leadData.associateId || leadData.assignedToId || (typeof leadData.assignedTo === 'string' ? leadData.assignedTo : leadData.assignedTo?._id || leadData.assignedTo?.id);
      if (aid && associates.length > 0) {
        // Try matching by _id or associateId
        const match = associates.find(a => a.id === aid || a.associateId === aid || a.fullData?.associateId === aid);
        if (match) setSelectedAssociate(match);
      }
    }
  }, [leadData, customers, associates, inquiryStatuses, leadStatuses]);

  const handleBack = () => history.push("/app/lead/LeadList");

  const handleChange = (e) => {
    let { name, value } = e.target;

    if (name === 'phone' || name === 'whatsapp') {
      value = value.replace(/\D/g, '').slice(0, 10);

      if (value && value.length < 10) {
        if (name === 'phone') setMobileError('Mobile number must be 10 digits');
        else setWhatsappError('WhatsApp number must be 10 digits');
      } else if (value && !validateMobile(value)) {
        if (name === 'phone') setMobileError('Mobile number invalid');
        else setWhatsappError('Mobile number invalid');
      } else {
        if (name === 'phone') setMobileError('');
        else setWhatsappError('');
      }
    }

    setFormData((p) => ({ ...p, [name]: value }));
    setErrors((p) => ({ ...p, [name]: "" }));
  };

  const handleCustomerSelect = (event, newValue) => {
    setSelectedCustomer(newValue);
    if (newValue) {
      // Auto-fill the form with customer data and disable fields
      setFormData((prev) => ({
        ...prev,
        name: newValue.name || "",
        email: newValue.email || "",
        phone: newValue.mobile || "",
        whatsapp: newValue.whatsappNumber || "",
        company: newValue.companyName || newValue.createdBy || "",
        designation: newValue.profession || "",
      }));
    } else {
      // Clear customer selection and reset form
      setSelectedCustomer(null);
      if (!leadData) { // Only reset if not editing existing lead
        setFormData((prev) => ({
          ...prev,
          name: "",
          email: "",
          phone: "",
          whatsapp: "",
          company: "",
          designation: "",
        }));
      }
    }
  };

  const handleAssociateChange = (e, value) => {
    setSelectedAssociate(value);
    if (value) {
      setFormData(prev => ({
        ...prev,
        assignedTo: value.name || '',
        assignedToId: value.id || value.associateId || '',
        assignedMobile: value.mobile || '',
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        assignedTo: '',
        assignedToId: '',
        assignedMobile: '',
      }));
    }
  };

  const validate = () => {
    const e = {};
    if (!selectedCustomer) e.customer = "Customer selection is required";
    // Source and Interested In are no longer required
    // Budget validation removed because field is hidden

    if (formData.phone && !validateMobile(formData.phone)) {
      e.phone = "Mobile number invalid";
    }
    if (formData.whatsapp && !validateMobile(formData.whatsapp)) {
      e.whatsapp = "Mobile number invalid";
    }
    return e;
  };

  const compact = (obj) =>
    Object.fromEntries(
      Object.entries(obj).filter(
        ([, v]) => v !== undefined && v !== null && !(typeof v === "string" && v.trim() === "")
      )
    );

  const buildPayload = ({ isDeleted = false, isActive = formData.isActive }) => {
    const payload = {
      _id: meta._id,
      leadNo: meta.leadNo,
      customerId: selectedCustomer?._id || selectedCustomer?.id, // Add customer ID
      name: formData.name,
      profession: formData.designation || undefined,
      interestedIn: formData.interestedIn || undefined,
      budgetUpto: formData.budget,
      projectInterestedIn: meta.projectInterestedIn,
      source: formData.source || undefined,
      stage: meta.stage,
      status: formData.status,
      contact: compact({
        phone: formData.phone,
        whatsapp: formData.whatsapp || formData.phone,
      }),
      address: compact({
        addressLine: formData.address || formData.location,
        city: formData.city || "",
        state: formData.state || "",
      }),
      followUp: formData.nextFollowUp
        ? {
          nextFollowUpDate: formData.nextFollowUp,
          lastFollowUpRemark: formData.remark || meta.followUp?.lastFollowUpRemark || "",
          lastFollowUpDate: formData.followUpDate || meta.followUp?.lastFollowUpDate || "",
          followUpType: formData.followUpStatus || meta.followUp?.followUpType || "HOT",
        }
        : {
          nextFollowUpDate: "",
          lastFollowUpRemark: formData.remark || meta.followUp?.lastFollowUpRemark || "",
          lastFollowUpDate: formData.followUpDate || meta.followUp?.lastFollowUpDate || "",
          followUpType: formData.followUpStatus || meta.followUp?.followUpType || "HOT",
        },
      remarks: {
        remark1: formData.requirement || "",
        remark2: meta.remarks?.remark2 || "",
        generalRemark: formData.remark || "",
      },
      assignedTo: formData.assignedTo
        ? {
          name: formData.assignedTo,
          phone: formData.assignedMobile || meta.assignedTo?.phone || "",
        }
        : meta.assignedTo,
      associateId: formData.assignedToId || meta.associateId || undefined,
      createdBy: meta.createdBy || formData.company || "YES ",
      updatedBy: "Admin",
      reference: formData.reference || meta.reference || "",
      isDeleted,
      isActive,
    };
    return payload;
  };

  const base_url = process.env.REACT_APP_API_BASE_URL

  const putUrl = (id) => `${base_url}/api/leads/${encodeURIComponent(id)}`;

  const doUpdate = async (payload) => {
    const id = meta.id || meta._id;
    if (!id) throw new Error("Lead ID missing");
    const res = await fetch(putUrl(id), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(text || `HTTP ${res.status}`);
    }
    return res.json().catch(() => ({}));
  };

  const handleSubmit = async () => {
    setServerError("");
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    try {
      setSaving(true);
      const payload = buildPayload({ isDeleted: false, isActive: formData.isActive });
      await doUpdate(payload);
      showAlert("Lead updated successfully!", "Success");
      setTimeout(() => history.push("/app/lead/LeadList"), 1500);
    } catch (err) {
      setServerError(err?.message || "Failed to update lead");
    } finally {
      setSaving(false);
    }
  };

  const handleSoftDelete = async () => {
    setConfirmDialog({
      open: true,
      title: 'Confirm Deletion',
      message: 'Mark this lead as deleted?',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'error',
      action: async () => {
        setServerError("");
        try {
          setDeleting(true);
          const payload = buildPayload({ isDeleted: true, isActive: formData.isActive });
          await doUpdate(payload);
          showAlert("Lead deleted (soft delete) successfully!", "Success");
          setTimeout(() => history.push("/app/lead/LeadList"), 1500);
        } catch (err) {
          setServerError(err?.message || "Failed to delete lead");
        } finally {
          setDeleting(false);
        }
      }
    });
  };

  const handleToggleActive = async (nextActive) => {
    setServerError("");
    try {
      setToggling(true);
      const payload = buildPayload({ isDeleted: false, isActive: nextActive });
      await doUpdate(payload);
      showAlert(nextActive ? "Lead activated" : "Lead deactivated", "Success");
      setFormData((p) => ({ ...p, isActive: nextActive }));
    } catch (err) {
      setServerError(err?.message || "Failed to toggle active state");
    } finally {
      setToggling(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 2, mb: 3, bgcolor: "#1976d2", color: "#fff" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton onClick={handleBack} sx={{ color: "#fff" }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Edit Lead
          </Typography>
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
        {serverError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {serverError}
          </Alert>
        ) : null}

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: "#1976d2" }}>
            Personal Information
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {/* {selectedCustomer && (
            <Alert severity="info" sx={{ mb: 2, fontSize: '0.875rem' }}>
              Customer details are auto-filled and cannot be modified. You can only edit lead-specific information below.
            </Alert>
          )} */}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                fullWidth
                options={customers}
                getOptionLabel={(option) => `${option.name} (${option.customerId}) - ${option.mobile}`}
                value={selectedCustomer}
                onChange={handleCustomerSelect}
                loading={loadingCustomers}
                open={customerSearchOpen}
                onOpen={() => setCustomerSearchOpen(true)}
                onClose={() => setCustomerSearchOpen(false)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Customer "
                    size="small"
                    placeholder="Search and select customer..."
                    required
                    error={!selectedCustomer && errors.customer}
                    helperText={!selectedCustomer && errors.customer ? "Customer selection is required" : ""}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon sx={{ fontSize: 18 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {option.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.customerId} • {option.mobile} • {option.email}
                      </Typography>
                    </Box>
                  </li>
                )}
                noOptionsText="No customers found"
                clearOnEscape
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label={selectedCustomer ? "Customer Name (Auto-filled)" : "Full Name"}
                name="name"
                value={formData.name}
                onChange={handleChange}
                size="small"
                required
                error={!!errors.name}
                helperText={errors.name}
                disabled={!!selectedCustomer}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}

              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                size="small"
                disabled={!!selectedCustomer}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}

              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Phone Number"
                name="phone"
                value={formData.phone}
                onChange={(e) => {
                  let val = e.target.value.replace(/\D/g, '').slice(0, 10);
                  handleChange({ target: { name: 'phone', value: val } });
                }}
                size="small"
                required
                error={!!errors.phone || !!mobileError}
                helperText={errors.phone || mobileError || "10 digits (starts with 6-9)"}
                disabled={!!selectedCustomer}
                inputProps={{
                  maxLength: 10,
                  pattern: '[6-9][0-9]{9}',
                  inputMode: 'numeric',
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}

              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="WhatsApp Number"
                name="whatsapp"
                value={formData.whatsapp}
                onChange={(e) => {
                  let val = e.target.value.replace(/\D/g, '').slice(0, 10);
                  handleChange({ target: { name: 'whatsapp', value: val } });
                }}
                size="small"
                error={!!errors.whatsapp || !!whatsappError}
                helperText={errors.whatsapp || whatsappError || "10 digits (starts with 6-9)"}
                disabled={!!selectedCustomer}
                inputProps={{
                  maxLength: 10,
                  pattern: '[6-9][0-9]{9}',
                  inputMode: 'numeric',
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <WhatsAppIcon sx={{ fontSize: 18, color: "#25D366" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                size="small"
                disabled={!!selectedCustomer}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BusinessIcon sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Designation"
                name="designation"
                value={formData.designation}
                onChange={handleChange}
                size="small"
                disabled={!!selectedCustomer}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <WorkIcon sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size='small'>
                <InputLabel>Budget</InputLabel>
                <Select
                  name='budget'
                  value={formData.budget}
                  onChange={handleChange}
                  label='Budget'
                >
                  {budgetOptionsFromApi.map((option) => (
                    <MenuItem key={option.id} value={option.value}>
                      {option.budgetRange}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: "#1976d2" }}>
            Lead Information
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl
                fullWidth
                size='small'
              >
                <InputLabel>Source </InputLabel>
                <Select
                  name='source'
                  value={formData.source || ''}
                  onChange={handleChange}
                  label='Source'
                >
                  {sources.map((source) => (
                    <MenuItem key={source.id} value={source.name}>
                      {source.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size='small'>
                <InputLabel>Status</InputLabel>
                <Select
                  name='status'
                  value={formData.status}
                  onChange={handleChange}
                  label='Status'
                >
                  {leadStatuses.map((option) => (
                    <MenuItem key={option.id} value={option.leadStatus}>
                      {option.leadStatus}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid> */}

            <Grid item xs={12} sm={6} md={4}>
              <FormControl
                fullWidth
                size='small'
              >
                <InputLabel>Interested In</InputLabel>
                <Select
                  name='interestedIn'
                  value={formData.interestedIn}
                  onChange={handleChange}
                  label='Interested In'
                >
                  {leadInterestedOptions.map((option) => (
                    <MenuItem key={option.id} value={option.leadInterested}>
                      {option.leadInterested}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* <Grid item xs={12} sm={6} md={4}>
              <FormControl
                fullWidth
                size='small'
                required
                error={!!errors.budget}
              > */}
            {/* <InputLabel>Budget *</InputLabel>
                <Select
                  name='budget'
                  value={formData.budget}
                  onChange={handleChange}
                  label='Budget *'
                >
                  {budgetOptionsFromApi.map((option) => (
                    <MenuItem key={option.id} value={option.value}>
                      {option.budgetRange}
                    </MenuItem>
                  ))}
                </Select> */}
            {/* </FormControl>
              {errors.budget ? (
                <Typography variant='caption' color='error'>
                  {errors.budget}
                </Typography>
              ) : null}
            </Grid> */}

            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                fullWidth
                size="small"
                options={associates}
                getOptionLabel={(option) => option.displayLabel || `${option.name || ''} — ${option.mobile || ''}`}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                loading={loadingAssociates}
                value={selectedAssociate}
                onChange={handleAssociateChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assigned To"
                    placeholder="Search Associate"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ fontSize: 18 }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <>
                          {loadingAssociates ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Assigned Name"
                name="assignedTo"
                value={formData.assignedTo}
                size="small"
                disabled
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Assigned Mobile"
                name="assignedMobile"
                value={formData.assignedMobile}
                size="small"
                disabled
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid> */}
          </Grid>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: "#1976d2" }}>
            Location Details
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>State</InputLabel>
                <Select name="state" value={formData.state} onChange={handleChange} label="State">
                  {stateOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField fullWidth label="City" name="city" value={formData.city} onChange={handleChange} size="small" />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Pin Code"
                name="location"
                value={formData.location}
                onChange={(e) => {
                  let val = e.target.value.replace(/\D/g, '').slice(0, 6);
                  handleChange({ target: { name: 'location', value: val } });
                }}
                size="small"
                inputProps={{
                  maxLength: 6,
                  pattern: '[0-9]{6}',
                  inputMode: 'numeric',
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOnIcon sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Full Address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                size="small"
                multiline
                rows={2}
                placeholder="Enter complete address"
              />
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: "#1976d2" }}>
            Additional Information
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Requirement Details"
                name="requirement"
                value={formData.requirement}
                onChange={handleChange}
                size="small"
                multiline
                rows={3}
                placeholder="Describe the customer's requirements..."
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Remark"
                name="remark"
                value={formData.remark}
                onChange={handleChange}
                size="small"
                multiline
                rows={2}
                placeholder="Any additional notes..."
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reference"
                name="reference"
                value={formData.reference}
                onChange={handleChange}
                size="small"
                placeholder="e.g., Friend - Amit Verma"
              />
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mt: 4, flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button variant="outlined" onClick={handleBack} disabled={saving || deleting || toggling}>
              Cancel
            </Button>
          </Box>

          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant={formData.isActive ? "outlined" : "contained"}
              color={formData.isActive ? "warning" : "success"}
              onClick={() => handleToggleActive(!formData.isActive)}
              disabled={saving || deleting || toggling}
            >
              {toggling ? <CircularProgress size={18} color="inherit" /> : formData.isActive ? "Mark Inactive" : "Mark Active"}
            </Button>

            <Button variant="outlined" color="error" onClick={handleSoftDelete} disabled={saving || deleting || toggling}>
              {deleting ? <CircularProgress size={18} color="inherit" /> : "Delete"}
            </Button>

            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
              onClick={handleSubmit}
              disabled={saving || deleting || toggling}
              sx={{ textTransform: "none" }}
            >
              {saving ? "Updating..." : "Update Lead"}
            </Button>
          </Box>
        </Box>
      </Paper>

      <AlertModal
        open={alertData.open}
        onClose={() => setAlertData({ ...alertData, open: false })}
        title={alertData.type}
        message={alertData.message}
        type={alertData.type.toLowerCase() === 'success' ? 'success' : alertData.type.toLowerCase() === 'error' ? 'error' : alertData.type.toLowerCase() === 'warning' ? 'warning' : 'info'}
      />

      <ConfirmModal
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
        onConfirm={() => {
          if (confirmDialog.action) confirmDialog.action();
          setConfirmDialog({ ...confirmDialog, open: false });
        }}
        type={confirmDialog.type}
      />
    </Box>
  );
};

export default EditLead;
